import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { requestPasswordResetWithLambda, resetPasswordWithLambda } from '../repository/authRepository';

const PasswordReset: React.FC = () => {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [variant, setVariant] = useState<'success' | 'error'>('success');
  const [isLoading, setIsLoading] = useState(false);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await requestPasswordResetWithLambda(username);
      if ('error' in result) {
        setMessage(result.error || 'Failed to send reset email');
        setVariant('error');
      } else {
        setMessage('Password reset email sent. Check your inbox.');
        setVariant('success');
        setUsername('');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match');
      setVariant('error');
      return;
    }

    if (newPassword.length < 8) {
      setMessage('Password must be at least 8 characters long');
      setVariant('error');
      return;
    }

    setIsLoading(true);

    try {
      if (!resetToken) {
        setMessage('No reset token provided');
        setVariant('error');
        return;
      }

      const result = await resetPasswordWithLambda(resetToken, newPassword);
      if ('error' in result) {
        setMessage(result.error || 'Failed to update password');
        setVariant('error');
      } else {
        setMessage('Password updated successfully. Redirecting to login...');
        setVariant('success');
        setTimeout(() => navigate('/login'), 2000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (resetToken) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h2>Reset your password</h2>
          {message && <div className={`notice ${variant}`}>{message}</div>}
          <form onSubmit={handleResetPassword}>
            <div className="auth-field">
              <label htmlFor="newPassword">New password</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                minLength={8}
              />
            </div>
            <div className="auth-field">
              <label htmlFor="confirmPassword">Confirm new password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                minLength={8}
              />
            </div>
            <div className="auth-actions">
              <button className="primary-button" type="submit" disabled={isLoading}>
                {isLoading ? 'Updating...' : 'Update password'}
              </button>
              <Link to="/login">Back to login</Link>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h2>Forgot your password?</h2>
        <p className="app-subtitle">Enter your username and we'll email you a reset link.</p>
        {message && <div className={`notice ${variant}`}>{message}</div>}
        <form onSubmit={handleRequestReset}>
          <div className="auth-field">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              required
            />
          </div>
          <div className="auth-actions">
            <button className="primary-button" type="submit" disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send reset link'}
            </button>
            <Link to="/login">Back to login</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordReset;
